import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import './dashboardPage.scss'
import { Layout, Menu, MenuProps, Space, theme, Input } from 'antd'
import Sider from 'antd/es/layout/Sider'
import { Content } from 'antd/es/layout/layout'
import { useSelector } from 'react-redux'
import NewOrder from '../../Components/NewOrder/newOrder.component'
import OrderTable from '../../Components/OrderTable/OrderTable'
import { axiosInstance } from '../../interceptor/axios_interceptor'
import { socket } from '../../socket'
import { Order } from '../../Types/order.interface'
import { OrderStatus } from '../../Types/api-structures'
import { usePrintAgent } from '../../Context/PrintAgentContext'
import { usePrintConfig, resolveRole } from '../../Context/PrintConfigContext'

export interface DistanceTimeToReach {
  distance: string
  expectedReachTime: number
}

export enum TrackResponseType {
  'UPGRADE' = 'UPGRADE',
  'NORMALTRACK' = 'NORMALTRACK',
  'PARTIALFOOD' = 'PARTIALFOOD',
  'ORDERCANCELLED' = 'ORDERCANCELLED',
  'ORDERCOMPLETED' = 'ORDERCOMPLETED',
  'ORDERNEARBY' = 'ORDERNEARBY',
  'ORDERREACHED' = 'ORDERREACHED'
}

export interface IRestaurantOrderItem {
  id: number

  order: any // or RestaurantOrder if you have the type imported

  categoryId: string

  subCategoryId?: string | null

  foodId: string

  foodStatus: 'Available' | 'Out of Stock' | 'Not Available' | 'Exceeded Time'

  quantity: number

  price: number

  gstAmount: number

  packagingCharges?: number | null

  createdAt: Date

  updatedAt: Date
}

interface TrackDetails {
  restaurantId: string
  trackType: TrackResponseType
  orderId: string
  message: {
    orderId: string
    currentUpdate: DistanceTimeToReach
    orderStatus: OrderStatus
  }
}

interface PartialUpdateType {
  restaurantId: string
  updatedPartialOrder: IRestaurantOrderItem[]
}

const options = [
  {
    label: 'New',
    shortLabel: 'New',
    key: 'PAYMENTCOMPLETED',
    count: 0
  },
  {
    label: 'OnTheWay',
    shortLabel: 'OnWay',
    key: 'ONLAZYTRACK',
    count: 0
  },
  {
    label: 'Preparing',
    shortLabel: 'Prep',
    key: 'PREPARING',
    count: 0
  },
  {
    label: 'Ready',
    shortLabel: 'Ready',
    key: 'READY',
    count: 0
  },
  {
    label: 'Serving',
    shortLabel: 'Serve',
    key: 'SERVING',
    count: 0
  },
  {
    label: 'Completed',
    shortLabel: 'Done',
    key: 'COMPLETED',
    count: 0
  }
]

const siderStyle: React.CSSProperties = {
  // overflow: 'auto',
  height: 'fit-content',
  boxShadow: '1px 1px 6px 2px #80808073',
  // flex: 1,
  // position: 'fixed',
  // insetInlineStart: 0,
  // scrollbarWidth: 'thin',
  // scrollbarColor: 'unset',
  margin: '20px 0px 10px 10px',

  borderRadius: '5px',
  paddingBottom: '10px'
  // zIndex: 1,
}
const DashboardPage = () => {
  const restaurantLive = useSelector((state: any) => state.restaurant.live)
  const reduxUser = useSelector((state: any) => state.user)
  const [orderOptionsList, setOrderOptionsList] =
    useState<typeof options>(options)
  const [allActiveOrders, setAllActiveOrders] = useState<Order[]>([])
  const navigate = useNavigate()
  const { sendPrintJob } = usePrintAgent()
  const { printConfig } = usePrintConfig()
  // Flat list of all menu food items from Redux store for cuisine lookup
  const menuCard: any[] = useSelector(
    (state: any) => state.menu?.liveMenuCard ?? []
  )

  const [orderOptions, setOrderOptions] = useState<string | undefined>('New')
  const [isSocketConnected, setIsSocketConnected] = useState(socket.connected)

  const [searchKOT, setSearchKOT] = useState<string>('')
  const [searchedOrderIds, setSearchedOrderIds] = useState<string[] | null>(null)
  const [isSearching, setIsSearching] = useState<boolean>(false)

  const handleSearchKOT = async (value: string) => {
    if (!value || value.length < 3) {
      setSearchedOrderIds(null)
      return
    }
    setIsSearching(true)
    try {
      const activeTabOption = options.find((opt) => opt.label === orderOptions)
      const orderStatuses = activeTabOption ? [activeTabOption.key] : []

      const res = await axiosInstance.post(
        `/api/vendor/kot/${reduxUser?.restaurantId}/${value}/order`,
        { orderStatuses }
      )
      if (res.data && res.data.responseCode === 0 && Array.isArray(res.data.orders) && res.data.orders.length > 0) {
        setSearchedOrderIds(res.data.orders.map((o: any) => o.orderId))
      } else {
        setSearchedOrderIds([])
      }
    } catch (err) {
      console.error(err)
      setSearchedOrderIds([])
    } finally {
      setIsSearching(false)
    }
  }

  const allActiveOrdersRef = useRef<Order[]>([])
  useEffect(() => {
    allActiveOrdersRef.current = allActiveOrders
  }, [allActiveOrders])

  const normalizeKOTServiceType = (rawServiceType?: string) => {
    const normalized = (rawServiceType || '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/[-\s]/g, '_')

    if (
      normalized === 'TAKEAWAY' ||
      normalized === 'TAKE_AWAY' ||
      normalized === 'TAKEWAY'
    ) {
      return 'PARCEL'
    }
    if (normalized === 'DINEIN' || normalized === 'DINE_IN') {
      return 'DINEIN'
    }
    if (normalized === 'DINEOUT' || normalized === 'DINE_OUT') {
      return 'DINEOUT'
    }
    return ''
  }

  // Build flat lookup maps from the Redux live menu:
  //   cuisineByFoodId: food_id → cuisine  (primary, fastest)
  //   cuisineByLabel:  normalized label → cuisine  (fallback when foodId changed)
  const { cuisineByFoodId, cuisineByLabel } = useMemo<{
    cuisineByFoodId: Record<string, string>
    cuisineByLabel: Record<string, string>
  }>(() => {
    const byId: Record<string, string> = {}
    const byName: Record<string, string> = {}
    const walkItems = (items: any[]) => {
      for (const node of items) {
        if (node.food_id && node.cuisine) byId[node.food_id] = node.cuisine
        if (node.label && node.cuisine) byName[node.label.trim().toLowerCase()] = node.cuisine
        if (node.children) walkItems(node.children)
        if (node.foodItems) walkItems(node.foodItems)
      }
    }
    walkItems(menuCard)
    return { cuisineByFoodId: byId, cuisineByLabel: byName }
  }, [menuCard])

  // Sends one KOT per distinct printer role derived from cuisine routing.
  // If routing is unconfigured, everything goes to a single KITCHEN job.
  const sendKOT = (order: Order, tableNumber?: string, empNo?: string) => {
    const routing = printConfig.cuisineRouting ?? {}
    const orderComments = (order.comments || '').trim() || '-'
    const dt = new Date(order.orderDateTime || Date.now())
    const date = dt.toLocaleDateString('en-GB').replace(/\//g, '/').slice(0, 8)
    const time = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })

    const basePayload = {
      documentType: 'KOT' as const,
      copies: 1,
      orderId: order.orderId,
      restaurant: order.restaurantName || reduxUser?.vendorInfo?.restaurantName || '',
      address: reduxUser?.vendorInfo?.restaurantAddress?.address || '',
      serviceType: normalizeKOTServiceType(order.serviceType),
      comments: orderComments,
      customerName: '',
      tableNo: tableNumber || '',
      empNo: empNo || '',
      date,
      time,
      subtotal: 0,
      tax: 0,
      discount: 0,
      total: 0,
      payMethod: ''
    }

    // Group items by the resolved printer role
    const groups = new Map<
      string,
      typeof basePayload & { printerRole: string; items: any[] }
    >()
    for (const item of order.orderItems || []) {
      // Resolve cuisine with three fallbacks (most-reliable → least):
      //  1. cuisineByFoodId:  matches current live menu by food_id  (fast, may miss if menu was rebuilt)
      //  2. cuisineByLabel:   matches live menu by normalized item name (covers rebuilt-menu case)
      //  3. item.cuisine:     value stored on the order item itself if the backend ever supplies it
      const itemLabel = ((item.label || '') as string).trim().toLowerCase()
      const cuisine =
        cuisineByFoodId[item.foodId] ||
        (itemLabel && cuisineByLabel[itemLabel]) ||
        (item as any).cuisine
      const role = resolveRole(cuisine, routing)
      if (!groups.has(role)) {
        groups.set(role, { ...basePayload, printerRole: role, items: [] })
      }
      groups.get(role)!.items.push({
        qty: Number(item.quantity || 1),
        name: item.label || item.foodId || 'Item',
        price: Number(item.price || 0),
        notes: item.comments || ''
      })
    }

    groups.forEach((payload) => sendPrintJob(payload))

    // Send an additional master KOT containing ALL items if configured
    if (printConfig.kotPrinter) {
      const allItems = (order.orderItems || []).map(item => ({
        qty: Number(item.quantity || 1),
        name: item.label || item.foodId || 'Item',
        price: Number(item.price || 0),
        notes: item.comments || ''
      }))

      sendPrintJob({
        ...basePayload,
        printerRole: printConfig.kotPrinter,
        items: allItems
      })
    }
  }

  const orderOptionSelected = ({ key }: any) => {
    setOrderOptions(options.find((option) => option?.key === key)?.label)
    console.log(orderOptions)
  }

  // Fetch all active orders on mount
  useEffect(() => {
    const fetchActiveOrders = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/vendor/getActiveOrdersByRestaurant/${reduxUser?.restaurantId}`
        )
        console.log('Active orders:', response.data)
        setAllActiveOrders(response.data.orders || [])
      } catch (error) {
        console.log('Error fetching active orders:', error)
      }
    }

    if (reduxUser?.restaurantId) {
      fetchActiveOrders()
    }
  }, [reduxUser?.restaurantId])

  // Track socket connection for fallback polling
  useEffect(() => {
    const handleConnect = () => setIsSocketConnected(true)
    const handleDisconnect = () => setIsSocketConnected(false)

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)

    return () => {
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
    }
  }, [])

  // 30-Second Polling Fallback during disconnect
  useEffect(() => {
    let pollInterval: NodeJS.Timeout
    if (!isSocketConnected && restaurantLive === 'LIVE' && reduxUser?.restaurantId) {
      console.log('Socket disconnected while LIVE. Starting 30s fallback active orders polling...')
      pollInterval = setInterval(async () => {
        try {
          const response = await axiosInstance.get(
            `/api/vendor/getActiveOrdersByRestaurant/${reduxUser.restaurantId}`
          )
          if (response.data?.orders) {
            console.log('[Fallback Polling] Fetched active orders:', response.data.orders)
            setAllActiveOrders(response.data.orders)
          }
        } catch (error) {
          console.error('[Fallback Polling] Failed to fetch orders:', error)
        }
      }, 30000)
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [isSocketConnected, restaurantLive, reduxUser?.restaurantId])

  // WebSocket listeners for real-time order updates
  useEffect(() => {
    // Inline handlers so they always close over the latest stable references
    const handleTrackUpdateLocal = (trackData: TrackDetails) => {
      const { trackType, orderId, message } = trackData
      console.log(
        `[respondOrderTrackToVendor] trackType=${trackType} orderId=${orderId} orderStatus=${message?.orderStatus}`
      )

      switch (trackType) {
        case TrackResponseType.UPGRADE:
          setAllActiveOrders((prevOrders) => {
            const exists = prevOrders.some((o) => o.orderId === orderId)
            if (!exists) {
              // Order not in local state yet — fetch it
              setTimeout(() => {
                fetchUpdatedOrderDetails(orderId).catch((err) =>
                  console.error('Error fetching upgraded order details', err)
                )
              }, 0)
              return prevOrders
            }
            return prevOrders.map((order) =>
              order.orderId === orderId
                ? {
                  ...order,
                  orderStatus: message.orderStatus,
                  awaitingTrack: false,
                  orderDistance: message.currentUpdate?.distance,
                  orderReachTime: message.currentUpdate?.expectedReachTime
                    ? message.currentUpdate.expectedReachTime
                    : order.orderReachTime
                }
                : order
            )
          })
          break

        case TrackResponseType.NORMALTRACK:
          setAllActiveOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderId === orderId
                ? {
                  ...order,
                  orderDistance: message.currentUpdate?.distance,
                  orderReachTime: message.currentUpdate?.expectedReachTime
                    ? message.currentUpdate.expectedReachTime
                    : order.orderReachTime
                }
                : order
            )
          )
          break

        case TrackResponseType.ORDERCANCELLED:
          setAllActiveOrders((prevOrders) =>
            prevOrders.filter((order) => order.orderId !== orderId)
          )
          break

        case TrackResponseType.ORDERCOMPLETED:
          setAllActiveOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderId === orderId
                ? {
                  ...order,
                  orderStatus: OrderStatus.COMPLETED,
                  highlightLevel: null
                }
                : order
            )
          )
          break

        case TrackResponseType.ORDERNEARBY:
          setAllActiveOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderId === orderId
                ? {
                  ...order,
                  ...(message.orderStatus ? { orderStatus: message.orderStatus } : {}),
                  orderDistance: message.currentUpdate?.distance,
                  orderReachTime: message.currentUpdate?.expectedReachTime
                    ? message.currentUpdate.expectedReachTime
                    : order.orderReachTime,
                  highlightLevel: 'nearby' as const
                }
                : order
            )
          )
          break

        case TrackResponseType.ORDERREACHED:
          setAllActiveOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderId === orderId
                ? {
                  ...order,
                  ...(message.orderStatus ? { orderStatus: message.orderStatus } : {}),
                  orderDistance: message.currentUpdate?.distance,
                  orderReachTime: message.currentUpdate?.expectedReachTime
                    ? message.currentUpdate.expectedReachTime
                    : order.orderReachTime,
                  highlightLevel: 'reached' as const
                }
                : order
            )
          )
          break

        default:
          console.warn(`Unknown track type: ${trackType}`)
      }
    }

    const handlePartialOrderUpdateLocal = (
      partialUpdate: PartialUpdateType
    ) => {
      const { restaurantId, updatedPartialOrder } = partialUpdate

      if (restaurantId !== reduxUser?.restaurantId) {
        console.log('Partial update for different restaurant, ignoring')
        return
      }

      if (!updatedPartialOrder || updatedPartialOrder.length === 0) {
        console.log('No items in partial update')
        return
      }

      const itemsByOrder = updatedPartialOrder.reduce(
        (acc, item) => {
          const oid = item.order?.orderId || item.order
          if (!acc[oid]) acc[oid] = []
          acc[oid].push(item)
          return acc
        },
        {} as Record<string, IRestaurantOrderItem[]>
      )

      setAllActiveOrders((prevOrders) =>
        prevOrders.map((order) => {
          const updatedItems = itemsByOrder[order.orderId]
          if (!updatedItems) return order

          const hasUnavailableItems = updatedItems.some(
            (item) =>
              item.foodStatus === 'Out of Stock' ||
              item.foodStatus === 'Not Available' ||
              item.foodStatus === 'Exceeded Time'
          )

          const updatedOrderItems = order.orderItems.map((orderItem) => {
            const partialItem = updatedItems.find(
              (pItem) => pItem.foodId === orderItem.foodId
            )
            if (partialItem) {
              return {
                ...orderItem,
                foodStatus: partialItem.foodStatus as
                  | 'Available'
                  | 'Out of Stock'
                  | 'Not Available'
                  | 'Exceeded Time',
                price: partialItem.price ?? orderItem.price,
                gstAmount: partialItem.gstAmount ?? orderItem.gstAmount,
                packagingCharges:
                  partialItem.packagingCharges ?? orderItem.packagingCharges,
                quantity: partialItem.quantity ?? orderItem.quantity
              }
            }
            return orderItem
          })

          return {
            ...order,
            orderItems: updatedOrderItems,
            hasPartialFood: hasUnavailableItems
          }
        })
      )
    }

    // Listen for new orders
    socket.on('neworder', (data: Order[]) => {
      console.log('New orders received:', data)
      setAllActiveOrders((prevOrders) => {
        const combinedOrders = [...prevOrders, ...data]
        const uniqueOrders = Array.from(
          new Map(
            combinedOrders.map((order) => [order.orderId, order])
          ).values()
        )
        return uniqueOrders
      })
      if (data.length > 0) {
        setOrderOptions('New')
        navigate('/vendor/app/dashboard')
      }
    })

    // Listen for order cancellations
    socket.on('ordercancelled', (orderId: string) => {
      console.log('Order cancelled:', orderId)
      setAllActiveOrders((prevOrders) =>
        prevOrders.filter((order) => order.orderId !== orderId)
      )
    })

    // Listen for order status updates
    socket.on('orderStatusUpdate', (updatedOrder: Order) => {
      console.log('Order status updated:', updatedOrder)
      setAllActiveOrders((prevOrders) => {
        const existingIndex = prevOrders.findIndex(
          (order) => order.orderId === updatedOrder.orderId
        )
        if (existingIndex !== -1) {
          const newOrders = [...prevOrders]
          const existingOrder = prevOrders[existingIndex]
          newOrders[existingIndex] = {
            ...updatedOrder,
            orderDistance:
              existingOrder.orderDistance || updatedOrder.orderDistance,
            orderReachTime:
              existingOrder.orderReachTime || updatedOrder.orderReachTime,
            highlightLevel:
              existingOrder.highlightLevel || updatedOrder.highlightLevel
          }
          return newOrders
        } else {
          return [...prevOrders, updatedOrder]
        }
      })
    })

    // Listen for track details from vendor
    socket.on('respondOrderTrackToVendor', (trackDetails: TrackDetails) => {
      console.log('Track details received:', trackDetails)
      handleTrackUpdateLocal(trackDetails)
    })

    socket.on('orderPartialUpdate', (partialUpdate: PartialUpdateType) => {
      console.log('Partial order update received:', partialUpdate)
      handlePartialOrderUpdateLocal(partialUpdate)
    })

    socket.on(
      'respondOrderTrackCancelToVendor',
      (data: { orderId: string; orderStatus: string; reason: string }) => {
        console.log('Track cancel details received:', data)
        alert(`Order ${data.orderId} cancelled: ${data.reason}`)
        setAllActiveOrders((prevOrders) =>
          prevOrders.filter((order) => order.orderId !== data.orderId)
        )
      }
    )

    return () => {
      socket.off('neworder')
      socket.off('ordercancelled')
      socket.off('orderStatusUpdate')
      socket.off('respondOrderTrackToVendor')
      socket.off('orderPartialUpdate')
      socket.off('respondOrderTrackCancelToVendor')
    }
  }, [reduxUser?.restaurantId, navigate])

  // Request active orders when restaurant goes live
  useEffect(() => {
    if (restaurantLive && reduxUser?.restaurantId) {
      socket.emit('requestActiveOrders', {
        restaurantId: reduxUser?.restaurantId
      })
    }
  }, [restaurantLive, reduxUser?.restaurantId])

  // Auto-update sidebar counts based on current orders
  useEffect(() => {
    const counts = allActiveOrders.reduce(
      (acc, order) => {
        const status = order.orderStatus?.toUpperCase()
        acc[status] = (acc[status] || 0) + 1
        return acc
      },
      {} as Record<string, number>
    )

    setOrderOptionsList((prevState) =>
      prevState.map((option) => ({
        ...option,
        count: counts[option.key] || 0
      }))
    )
  }, [allActiveOrders])

  // Helper function to filter orders by status
  const getOrdersForView = (status: string) => {
    let filtered = allActiveOrders.filter(
      (order) => order.orderStatus?.toUpperCase() === status.toUpperCase()
    )
    if (searchedOrderIds !== null) {
      filtered = filtered.filter((order) => searchedOrderIds.includes(order.orderId))
    }
    return filtered
  }

  // Remove order from active list (after accept/reject)
  const removeOrderFromActive = (orderId: string) => {
    setAllActiveOrders((prevOrders) =>
      prevOrders.filter((order) => order.orderId !== orderId)
    )
  }

  // Fetch updated order details (for partial food cases)
  const fetchUpdatedOrderDetails = async (orderId: string) => {
    try {
      const response = await axiosInstance.get(
        `/api/vendor/getOrderDetails/${orderId}`
      )

      if (response.data.responseCode === 0 && response.data.orderDetails) {
        setAllActiveOrders((prevOrders) => {
          const exists = prevOrders.some((order) => order.orderId === orderId)
          if (exists) {
            return prevOrders.map((order) =>
              order.orderId === orderId
                ? { ...response.data.orderDetails, hasPartialFood: false }
                : order
            )
          } else {
            return [
              ...prevOrders,
              { ...response.data.orderDetails, hasPartialFood: false }
            ]
          }
        })
        return response.data.orderDetails
      } else {
        throw new Error('Failed to fetch order details')
      }
    } catch (error) {
      console.error('Error fetching order details:', error)
      throw error
    }
  }

  // Polling fallback for newly accepted orders
  const startLazyTrackPolling = (orderId: string) => {
    let attempts = 0
    const maxAttempts = 10
    const pollInterval = 30000 // 30 seconds

    const poll = async () => {
      attempts++
      const currentOrders = allActiveOrdersRef.current
      const orderInTrack = currentOrders.find((o) => o.orderId === orderId)
      
      const isValidStatus =
        orderInTrack &&
        (String(orderInTrack.orderStatus).toUpperCase() === 'ONLAZYTRACK' ||
          String(orderInTrack.orderStatus).toUpperCase() === 'PREPARING')

      if (isValidStatus) {
        console.log(`[Lazy Track Fallback] Order ${orderId} is safely tracked. Stopping poll.`)
        return // Success, stop polling
      }

      console.log(
        `[Lazy Track Fallback] Order ${orderId} not found or stuck. Attempt ${attempts}/${maxAttempts}. Fetching...`
      )
      
      try {
        const res = await axiosInstance.get(
          `/api/vendor/getActiveOrdersByRestaurant/${reduxUser?.restaurantId}`,
          { headers: { 'X-Hide-Loader': 'true' } }
        )
        if (res.data?.orders) {
          setAllActiveOrders(res.data.orders)
          // Immediately check if the fresh response contains it
          const freshOrder = res.data.orders.find((o: any) => o.orderId === orderId)
          const isFreshValid =
            freshOrder &&
            (String(freshOrder.orderStatus).toUpperCase() === 'ONLAZYTRACK' ||
              String(freshOrder.orderStatus).toUpperCase() === 'PREPARING')
              
          if (isFreshValid) {
            console.log(`[Lazy Track Fallback] Order ${orderId} found after API fetch.`)
            return // Success
          }
        }
      } catch (err) {
        console.error('Lazy track fallback fetch failed:', err)
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, pollInterval)
      } else {
        console.log(`Failed to track order ${orderId} after ${maxAttempts} attempts. (Future enhancement: make admin API call to verify status)`)
      }
    }

    // Start first poll after 30 seconds
    setTimeout(poll, pollInterval)
  }

  const handlePrintKOT = async (orderId: string, tableNumber?: string, empNo?: string) => {
    let orderToPrint = allActiveOrders.find((o) => o.orderId === orderId)
    if (!orderToPrint) {
      orderToPrint = (await fetchUpdatedOrderDetails(orderId)) as Order
    }

    sendKOT(orderToPrint, tableNumber, empNo)
  }

  const handlePrintBill = async (orderId: string, tableNumber?: string, empNo?: string) => {
    let orderToPrint = allActiveOrders.find((o) => o.orderId === orderId)
    if (!orderToPrint) {
      orderToPrint = (await fetchUpdatedOrderDetails(orderId)) as Order
    }

    const dt = new Date(orderToPrint.orderDateTime)
    const date = dt.toLocaleDateString('en-GB').replace(/\//g, '/').slice(0, 8) // DD/MM/YY
    const time = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })

    const tax = Number(orderToPrint.gst || 0)
    const discount = Number(orderToPrint.discountAmount || 0)
    const packaging = Number(orderToPrint.packagingCharges || 0)
    const packagingGst = Number(orderToPrint.packagingGstTotal || 0)
    const total = Number(orderToPrint.totalAmount || 0)
    
    // Fallback if backend doesn't send orderTotalWithoutDiscount
    const subtotal = Number(orderToPrint.orderTotalWithoutDiscount || 0) || (total + discount - tax - packaging - packagingGst)
    const cgst = Number((tax / 2).toFixed(2))
    const sgst = Number((tax / 2).toFixed(2))

    const payload = {
      documentType: 'INVOICE' as const,
      printerRole: printConfig.invoicePrinter || 'Bill',
      copies: 1,
      orderId: orderToPrint.orderId,
      restaurant: orderToPrint.restaurantName || reduxUser?.vendorInfo?.restaurantName || 'FoodTolls',
      gstin: reduxUser?.vendorInfo?.gstin || '',
      address: reduxUser?.vendorInfo?.restaurantAddress?.address || '',
      serviceType: orderToPrint.serviceType || '',
      comments: (orderToPrint.comments || '').trim() || '',
      date,
      time,
      tableNo: tableNumber || '',
      empNo: empNo || '',
      subtotal: subtotal > 0 ? subtotal : total,
      orderTotalWithoutDiscount: subtotal,
      tax,
      cgst,
      sgst,
      discount,
      discountAmount: discount,
      packagingCharges: packaging,
      packagingGstTotal: packagingGst,
      total,
      items: (orderToPrint.orderItems || []).map(item => ({
        qty: Number(item.quantity || 1),
        name: item.label || item.foodId || 'Item',
        price: Number(item.price || 0),
        notes: item.comments || '',
        originalBasePrice: item.originalBasePrice,
        discountAmount: item.discountAmount
      }))
    }

    sendPrintJob(payload)
  }

  // Handle status transitions
  const handleStatusTransition = async (
    orderId: string,
    nextStatus: OrderStatus,
    otp?: string,
    tableNumber?: string
  ) => {
    try {
      if (nextStatus === OrderStatus.SERVING) {
        // Validate OTP provided by user before moving to SERVING
        const validateResponse = await axiosInstance.post(
          '/api/orders/validateOrderOTP',
          {
            orderId,
            orderOtp: otp ? Number(otp) : undefined
          }
        )
        if (!validateResponse.data.otpValid) {
          throw new Error('Invalid OTP. Please check and try again.')
        }

        // OTP valid — update status FIRST so it's not blocked by printing/instructions
        const updateResponse = await axiosInstance.post(
          '/api/vendor/updateOrderStatus',
          {
            orderId,
            orderStatus: nextStatus
          }
        )
        if (updateResponse.data.responseCode === 0) {
          setAllActiveOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderId === orderId
                ? { ...order, orderStatus: OrderStatus.SERVING }
                : order
            )
          )
          console.log(`Order ${orderId} moved to ${nextStatus}`)

          // Print KOT for kitchen after successful OTP + status update.
          let orderToPrint = allActiveOrders.find((o) => o.orderId === orderId)
          if (!orderToPrint) {
            try {
              orderToPrint = (await fetchUpdatedOrderDetails(orderId)) as Order
            } catch (err) {
              console.error('Could not fetch order for KOT', err)
            }
          }

          if (orderToPrint) {
            sendKOT(orderToPrint, tableNumber)
          } else {
            console.warn(
              `KOT print skipped: could not resolve order payload for ${orderId}`
            )
          }
        } else {
          throw new Error('Failed to update order status')
        }
      } else {
        const response = await axiosInstance.post(
          '/api/vendor/updateOrderStatus',
          {
            orderId,
            orderStatus: nextStatus
          }
        )

        if (response.data.responseCode === 0) {
          // Update the order status in local state
          setAllActiveOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderId === orderId
                ? { ...order, orderStatus: OrderStatus[nextStatus] }
                : order
            )
          )
          console.log(`Order ${orderId} moved to ${nextStatus}`)
        } else {
          throw new Error('Failed to update order status')
        }
      }
    } catch (error) {
      console.error('Error updating order status:', error)
      throw error
    }
  }

  return (
    <div className='dashboardMainContainer'>
      <div className="custom-order-type-nav">
        <div className="nav-header">
          <h2>Order Type</h2>
        </div>
        <div className="nav-items-container">
          {orderOptionsList.map(option => (
            <div
              key={option.key}
              className={`nav-item ${orderOptions === option.label ? 'active' : ''}`}
              onClick={() => orderOptionSelected({ key: option.key })}
            >
              <span className="nav-item-label full-label">{option.label}</span>
              <span className="nav-item-label short-label">{option.shortLabel}</span>
              <span className="nav-item-count">{option.count}</span>
            </div>
          ))}
        </div>
      </div>
      <Layout className="dashboard-table-layout" style={{ minWidth: 0, width: '100%', maxWidth: '100%', background: 'transparent' }}>
        <Content
          style={{
            padding: '0 16px',
            margin: '20px 0',
            height: 'fit-content',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}
        >
          {orderOptions !== 'New' && (
            <div className="search-kot-container">
              <Input.Search
                id="search-kot-input"
                placeholder="Search KOT"
                allowClear
                enterButton="Search KOT"
                size="large"
                onSearch={handleSearchKOT}
                value={searchKOT}
                onChange={(e) => {
                  setSearchKOT(e.target.value)
                  if (!e.target.value) {
                    setSearchedOrderIds(null)
                  }
                }}
                loading={isSearching}
                className="search-kot-input"
              />
            </div>
          )}
          {orderOptions === 'New' && (
            <NewOrder
              orders={getOrdersForView('PAYMENTCOMPLETED')}
              restaurantLive={restaurantLive}
              reduxUser={reduxUser}
              onOrderRemove={removeOrderFromActive}
              onOrderAcceptedFallback={startLazyTrackPolling}
            />
          )}
          {orderOptions === 'OnTheWay' && (
            <OrderTable
              orders={getOrdersForView('ONLAZYTRACK')}
              onRefreshOrder={fetchUpdatedOrderDetails}
            />
          )}
          {orderOptions === 'Preparing' && (
            <OrderTable
              orders={getOrdersForView('PREPARING')}
              showActions={true}
              onPrintKOT={handlePrintKOT}
              onPrintBill={handlePrintBill}
              actionConfig={{
                buttonText: 'Move to Ready',
                nextStatus: OrderStatus.READY,
                onAction: handleStatusTransition
              }}
              onRefreshOrder={fetchUpdatedOrderDetails}
            />
          )}
          {orderOptions === 'Ready' && (
            <OrderTable
              orders={getOrdersForView('READY')}
              showActions={true}
              onPrintKOT={handlePrintKOT}
              onPrintBill={handlePrintBill}
              actionConfig={{
                buttonText: 'Start Serving',
                nextStatus: OrderStatus.SERVING,
                onAction: handleStatusTransition
              }}
              onRefreshOrder={fetchUpdatedOrderDetails}
            />
          )}
          {orderOptions === 'Serving' && (
            <OrderTable
              orders={getOrdersForView('SERVING')}
              showActions={true}
              onPrintKOT={handlePrintKOT}
              onPrintBill={handlePrintBill}
              actionConfig={{
                buttonText: 'Complete',
                nextStatus: OrderStatus.COMPLETED,
                onAction: handleStatusTransition
              }}
              onRefreshOrder={fetchUpdatedOrderDetails}
            />
          )}
          {orderOptions === 'Completed' && (
            <OrderTable
              orders={getOrdersForView('COMPLETED')}
              onPrintBill={handlePrintBill}
            />
          )}
        </Content>
      </Layout>
    </div>
  )
}

export default DashboardPage
